import React from "react";
import {Typography} from "@mui/material";
import {Grey6, Grey7, Grey17} from "../../theme/styles";
import type {Field, Section} from "./FormRenderer";

/**
 * The line of object types this form was built from, drawn as a chain.
 *
 * WHY A PICTURE AND NOT A SENTENCE. A record at the end of a four-type line carries seventeen
 * questions from four different places, and the form says so one field at a time -- "· from
 * Fastener" on each. That is the right answer when you are reading a field and the wrong one when
 * you are trying to understand the record: nowhere does the form say how many levels there are,
 * which order they sit in, or how much each contributed. This is that shape, once, beside the form
 * it describes.
 *
 * DERIVED FROM THE FIELDS THE FORM IS DRAWING, not fetched. The line could be asked for separately
 * -- `Get Object Type Line` answers exactly this -- and then the diagram and the form would be two
 * answers to one question, free to disagree after a revise, a re-parent or a form that was
 * replaced at one level. Reading the drawn fields means the picture cannot describe a form other
 * than the one on screen.
 *
 * ABSENT RATHER THAN EMPTY on a record with no line. Most object types have no parent, their fields
 * carry no DeclaredBy, and a diagram of one box saying "Document" is a frame around nothing.
 */

// TWO MEANINGS, TWO COLOURS. "This record's own type" and "the level you just clicked" are
// different facts about different boxes, and drawing both in the app's blue made the leaf look
// permanently selected -- so clicking it appeared to do nothing, and clicking anything else looked
// like two selections at once.
//
// Violet rather than green or amber: those already mean RELEASED and DEPRECATED on the status pill
// three inches away, and a box that reads as a status on a form about types is worse than a box
// with no colour at all.
export const LEVEL_BLUE = "#1997D6";
export const LEAF_VIOLET = "#7A5AA8";

/** The colour a level is drawn in -- and the colour its sections are outlined in on the form, so
 *  the box and the part of the form it points at are plainly the same thing. */
export function colourOf(isLeaf: boolean): string {
    return isLeaf ? LEAF_VIOLET : LEVEL_BLUE;
}

const BOX_HEIGHT = 46;
const GAP = 22;
const LEFT = 10;

export type Level = {usid: string; name: string; fields: number; sections: number};

/** The line, root first, from the order the fields are drawn in.
 *
 *  FIRST APPEARANCE IS THE ORDER. The server emits a record's fields ancestor-first -- that is what
 *  makes the form read as the line that built it -- so the order they are seen in IS the line, and
 *  nothing here has to sort or re-derive it. */
export function lineOf(fields: Field[], sections: Section[]): Level[] {
    const order: string[] = [];
    const byUsid = new Map<string, Level>();

    const see = (declared: {ObjectTypeUSID: string; Name: string} | null | undefined,
                 kind: "fields" | "sections") => {
        if (!declared?.ObjectTypeUSID) return;
        const usid = String(declared.ObjectTypeUSID);
        if (!byUsid.has(usid)) {
            byUsid.set(usid, {usid, name: declared.Name, fields: 0, sections: 0});
            order.push(usid);
        }
        byUsid.get(usid)![kind] += 1;
    };

    // Fields first, because their order is the line's order; sections only add their counts.
    for (const field of fields) see(field.DeclaredBy, "fields");
    for (const section of sections) see(section.DeclaredBy, "sections");

    return order.map(usid => byUsid.get(usid)!);
}

export default function FormLineDiagram({fields, sections, width = 236, selected, onSelect}: {
    fields: Field[]; sections: Section[];
    /** Which level is lit on the form, if any. */
    selected?: string | null;
    /** Clicking a level outlines the sections it contributed. Clicking it again puts the outline
     *  away: a highlight with no way off is one the reader has to navigate away to clear. The
     *  colour travels with the choice so the outline on the form matches the box that caused it. */
    onSelect?: (usid: string | null, colour?: string) => void;
    /** The pane's width. The boxes fill it, so widening the pane shows longer type names rather
     *  than more whitespace -- which is the only reason the pane is resizable. */
    width?: number;
}) {
    const levels = lineOf(fields, sections);

    // ONE LEVEL IS NOT A HIERARCHY. A record whose type has no parent gets no diagram rather than a
    // single box, which would take up a pane to say what the heading already says.
    if (levels.length < 2) return null;

    const height = levels.length * BOX_HEIGHT + (levels.length - 1) * GAP + 4;
    const boxWidth = Math.max(120, width - 28 - LEFT * 2);
    // Roughly the characters that fit: the boxes are 12px sans, so about 7px a character after the
    // 12px inset each side. Cheaper and steadier than measuring text, and the title has the rest.
    const room = Math.max(8, Math.floor((boxWidth - 24) / 6.6));

    return (
        <div style={{width, flexShrink: 0, padding: "0 14px 0 14px", boxSizing: "border-box",
                     overflow: "hidden"}}>
            <Typography style={{fontSize: 12, fontWeight: 700, color: Grey17, marginBottom: 2}}>
                Where these questions come from
            </Typography>
            <Typography style={{fontSize: 11, color: Grey6, marginBottom: 12, lineHeight: 1.45}}>
                This record&rsquo;s type specialises the one above it. Each level adds its own
                questions; the answers are all held here.
                {onSelect ? " Click a level to outline its questions on the form." : ""}
            </Typography>

            <svg width={boxWidth + LEFT * 2} height={height} role="img"
                 aria-label={`Type line: ${levels.map(one => one.name).join(" then ")}`}>
                {levels.map((level, index) => {
                    const y = index * (BOX_HEIGHT + GAP) + 2;
                    const leaf = index === levels.length - 1;
                    const lit = selected === level.usid;
                    return (
                        <g key={level.usid}
                           onClick={() => onSelect?.(lit ? null : level.usid, colourOf(leaf))}
                           style={{cursor: onSelect ? "pointer" : "default"}}>
                            {/* The connector is drawn by the level BELOW, so it only exists
                                between two boxes and never dangles under the last one. */}
                            {index > 0 && (
                                <line x1={LEFT + 18} y1={y - GAP} x2={LEFT + 18} y2={y}
                                      stroke="#C6CFDA" strokeWidth={1.5}/>
                            )}
                            <rect x={LEFT} y={y} width={boxWidth} height={BOX_HEIGHT} rx={5}
                                  fill={lit ? (leaf ? "rgba(122,90,168,0.18)" : "rgba(25,151,214,0.16)")
                                            : leaf ? "rgba(122,90,168,0.09)" : "#FFFFFF"}
                                  stroke={lit || leaf ? colourOf(leaf) : "#D8DDE4"}
                                  strokeWidth={lit ? 2 : leaf ? 1.5 : 1}/>
                            <text x={LEFT + 12} y={y + 19} fontSize={12}
                                  fontWeight={leaf ? 700 : 500} fill={Grey17}>
                                {level.name.length > room
                                    ? `${level.name.slice(0, room - 1)}…` : level.name}
                            </text>
                            <text x={LEFT + 12} y={y + 35} fontSize={10.5}
                                  fill={leaf ? LEAF_VIOLET : Grey7}
                                  fontWeight={leaf ? 600 : 400}>
                                {level.fields === 1 ? "1 question" : `${level.fields} questions`}
                                {leaf ? " · this record" : ""}
                            </text>
                            {/* The whole box is the target, not just its text -- an SVG <text> is
                                only hittable where the glyphs are, so clicking the empty half of a
                                box would do nothing. */}
                            <rect x={LEFT} y={y} width={boxWidth} height={BOX_HEIGHT} rx={5}
                                  fill="transparent"/>
                            {/* The title carries the name a truncated box cannot. */}
                            <title>{`${level.name} — ${level.fields} question`
                                    + `${level.fields === 1 ? "" : "s"}`}</title>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}
